import { Button, Card, CardContent, CardHeader, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link, List, ListItem, ListSubheader, Typography, useMediaQuery, useTheme } from "@material-ui/core";
import { Help } from "@material-ui/icons";
import firebase from "firebase/app";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useDocumentData } from "react-firebase-hooks/firestore";
import { Link as RouterLink } from "react-router-dom";

import { Collections } from "Collections";
import * as FirebaseConfigs from 'FirebaseConfigs';
import { FirebaseUtils } from "firebaseUtils/FirebaseUtils";
import { AnalyticsEvent } from "models/AnalyticsEvent";
import { AppRoles } from "models/AppRoles";
import { AppUser } from "models/AppUser";
import { EnqueueSnacbar } from "models/EnqueueSnacbar";
import { Nullable } from "models/Nullable";
import { Org } from "models/Org";
import { OrgStatistics } from "models/OrgStatistics";
import { OrgSubscription } from "models/OrgSubscription";
import { OrgUser } from "models/OrgUser";
import { StripeProduct } from "models/StripeProduct";
import { StripeProductPrice } from "models/StripeProductPrice";
import { StripeSubscription } from "models/StripeSubscription";
import { Paths } from "Paths";
import { formatMoney } from "screens/Offer/CreateOffer/SummaryTable";
import { WarningModal } from "screens/Users/OrgUserScreen";

import { useAppUser } from "./AppUserProvider";
import { LoadingButton } from "./LoadingButton";
import { useOrg } from "./OrgProvider";
import { useOrgUser } from "./OrgUserProvider";

// Checkout handler
async function subscribe(appUser: AppUser, orgUser: OrgUser, org: Org, priceId: string, enqueueSnackbar: EnqueueSnacbar) {
    const checkoutSession = {
        // automatic_tax: true,
        // tax_id_collection: true,
        // collect_shipping_address: true,
        allow_promotion_codes: true,
        line_items: [
            {
                price: priceId,
            }
        ],
        success_url: window.location.href,
        cancel_url: window.location.href,
        metadata: {
            orgId: org.id,
            orgUserId: orgUser.id,
            appUserId: appUser.id,
        },
    };

    const docRef = await FirebaseConfigs.db
        .collection(Collections.STRIPE_CUSTOMERS)
        .doc(appUser.id)
        .collection(Collections.STRIPE_CUSTOMER_CHECKOUT_SESSIONS)
        .add(checkoutSession)

    return new Promise<string>((resolve, reject) => {
        // Wait for the CheckoutSession to get attached by the extension
        docRef.onSnapshot((snap) => {
            const { error, url } = snap.data() as any;
            if (error) {
                enqueueSnackbar('Noe gikk feil under betalingen:' + error.message, { variant: 'error' })
                console.error(error)
                reject(error)
            }

            if (url) {
                console.log(url)
                resolve(url)
            }
        })
    })
}

// TODO: Implement this could function. This relies on the uid and pries are based on orgId
async function getCustomerPortalLink() {
    const functionRef = firebase
        .app()
        .functions('europe-west2')
        .httpsCallable('ext-firestore-stripe-subscriptions-createPortalLink');
    const { data } = await functionRef({
        returnUrl: window.location.href,
    });
    return data.url
}

type InfoButtonProps = {
    children?: React.ReactNode
    title: string
}

function InfoButton({ children, title }: InfoButtonProps) {
    const [show, setShow] = useState(false)
    return show ? (
        <WarningModal title={title} onClose={() => setShow(false)}>
            {children}
        </WarningModal>
    ) : (
        <IconButton color="secondary" onClick={() => setShow(true)}>
            <Help />
        </IconButton>
    )
}

export function setFreeStandardSubscription(value: boolean, orgId: string, enqueueSnackbar: EnqueueSnacbar) {
    return FirebaseConfigs
        .persistedDb
        .collection('OrgSubscriptions')
        .doc(orgId)
        .set({
            adminOverrides: {
                freeStandard: {
                    active: value,
                },
            },
        } as Partial<OrgSubscription>, { merge: true })
        .catch((error) => {
            FirebaseUtils.syncLogError(
                null,
                null,
                enqueueSnackbar,
                error,
                value ? AnalyticsEvent.ENABLE_FREE_ACCESS_ERROR : AnalyticsEvent.DISABLE_FREE_ACCESS_ERROR,
                {
                    contextEvent: value ? AnalyticsEvent.ENABLED_FREE_ACCESS : AnalyticsEvent.DISABLED_FREE_ACCESS,
                    orgId: orgId,
                },
            )
        })
}

export function PaymentManager() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'), { noSsr: true });
    const { enqueueSnackbar } = useSnackbar()
    const [orgDoc] = useOrg()
    const [orgUserDoc] = useOrgUser()
    const [appUserDoc] = useAppUser()

    const orgUser = orgUserDoc?.data || null
    const appUser = appUserDoc?.data || null
    const org = orgDoc?.data || null
    const orgId = org?.id
    const isAdmin = !!appUser?.appRoles.map[AppRoles.FlatEnum.ADMIN]

    const [customerPortalLink, setCustomerPortalLink] = useState("")
    const [products, setProducts] = useState<Nullable<(StripeProduct & { prices: StripeProductPrice[] })[]>>(null)
    const [subscriptions, setSubscriptions] = useState<Nullable<StripeSubscription[]>>(null)
    const [orgStatistics] = useDocumentData<OrgStatistics>(orgId ? FirebaseConfigs.persistedDb.collection('OrgStatistics').doc(orgId) : null)
    const [orgSubscription, loadingOrgSubscription] = useDocumentData<OrgSubscription>(orgId ? FirebaseConfigs.persistedDb.collection('OrgSubscriptions').doc(orgId) : null)
    const hasFreeStandardSubscription = orgSubscription?.adminOverrides.freeStandard.active

    useEffect(() => {
        FirebaseConfigs.db.collection(Collections.STRIPE_PRODUCTS)
            .where('active', '==', true)
            .get()
            .then(function (querySnapshot) {
                const promises = querySnapshot.docs.map(async function (prodDoc) {
                    const priceSnap = await prodDoc.ref.collection(Collections.STRIPE_PRODUCT_PRICES).get()
                    return {
                        ...prodDoc.data() as StripeProduct,
                        id: prodDoc.id,
                        prices: priceSnap.docs.map((priceDoc) => ({
                            ...priceDoc.data() as StripeProductPrice,
                            id: priceDoc.id,
                        }))
                    }
                })
                Promise.all(promises).then(data => setProducts(data))
            })

    }, [setProducts])

    useEffect(() => {
        if (orgId) {
            FirebaseConfigs.persistedDb
                .collectionGroup(Collections.STRIPE_CUSTOMER_SUBSCRIPTIONS)
                .where('metadata.orgId', '==', orgId)
                .where('status', '==', 'active')
                .get()
                .then(async function (querySnapshot) {
                    const promises = querySnapshot.docs.map(async function (subDoc) {
                        const data = subDoc.data()
                        return {
                            ...data,
                            id: subDoc.id,
                            product: {
                                ...(await data.product.get()).data(),
                                id: data.product.id,
                            },
                            price: {
                                ...(await data.price.get()).data(),
                                id: data.price.id,
                            },
                        } as StripeSubscription
                    })
                    Promise.all(promises).then(data => setSubscriptions(data))
                })
        }
    }, [orgId, setSubscriptions])

    return (
        <ListItem>
            {customerPortalLink ? (
                <Dialog
                    open
                    onClose={() => setCustomerPortalLink("")}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">Gå til kundeportal</DialogTitle>
                    <DialogContent>
                        Trykk på knappen under for å gå til kundeportalen. Der kan du starte, stoppe og endre abonnement og behandle betalingsinformasjonen din.
                    </DialogContent>
                    <DialogActions>
                        <Button color="primary" variant="contained" autoFocus href={customerPortalLink}>Gå til kundeportal</Button>
                        <Button color="secondary" onClick={() => setCustomerPortalLink("")}>Avbryt</Button>
                    </DialogActions>
                </Dialog>
            ) : null}
            <Card elevation={3} style={{ padding: 20, borderRadius: 18, width: isMobile ? '100%' : 'min(350px, 100%)', margin: 'auto' }}>
                <CardHeader
                    style={{ paddingBottom: 0 }}
                    title={
                        <div>
                            Abonnementer
                            <InfoButton title="Abonnementer">
                                <Typography>
                                    {'Abonnementet knyttes til brukeren som bestiller det. Kun den brukeren kan endre abonnementet i ettertid. Hvis du opplever problemer med bestilling/avbestilling, kan du '}
                                    <Link component={RouterLink} to={Paths.OWN_HELP_MESSAGES}>kontakte oss her</Link>
                                </Typography>
                            </InfoButton>
                        </div>
                    } />
                <CardContent style={{ paddingRight: 0, paddingLeft: 0, paddingTop: 0 }}>
                    <List disablePadding>
                        {
                            ((subscriptions == null) && (products === null)) ? (
                                <CircularProgress />
                            ) : (
                                <ListItem>
                                    {
                                        hasFreeStandardSubscription ? (
                                            <Typography variant="body2">
                                                En administrator har gitt organisasjonen din gratis tilgang til appen. Du trenger ikke et abonnement for øyeblikket
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2">
                                                Du må ha et abonnement for å lage nye utkast og anbud. <Link component={RouterLink} to={Paths.OWN_HELP_MESSAGES}>Kontakt oss</Link> for å avtale pris.
                                            </Typography>
                                        )
                                    }
                                </ListItem>
                            )}
                        {(loadingOrgSubscription || !isAdmin || !orgId) ? null : (
                            <ListItem>
                                <Button
                                    color={hasFreeStandardSubscription ? 'secondary' : 'primary'}
                                    variant="contained"
                                    fullWidth
                                    size="small"
                                    onClick={() => setFreeStandardSubscription(!hasFreeStandardSubscription, orgId, enqueueSnackbar)}
                                >{hasFreeStandardSubscription ? 'Deaktiver Gratis Tilgang' : 'Aktiver Gratis Tilgang'}</Button>
                            </ListItem>
                        )}
                        {subscriptions?.map((subscription) => {
                            const isSubscriptionManager = appUser?.id === subscription.metadata.appUserId

                            return (
                                <>
                                    <ListSubheader key={subscription.id + '-subheader'} style={{ lineHeight: '10px', marginTop: '20px' }}>{subscription.product.name} - {`${subscription.quantity || orgStatistics?.orgUserCount.current} x ${formatMoney(subscription.price.unit_amount / 100)}/${subscription.price.interval === 'month' ? 'mnd' : ''}`}</ListSubheader>
                                    <ListItem key={subscription.id}>
                                        {isSubscriptionManager ? (
                                            <LoadingButton
                                                size="small"
                                                color="secondary"
                                                ariaLabel="manage subscriptions"
                                                onClick={async () => {
                                                    await getCustomerPortalLink()
                                                        .then(setCustomerPortalLink)
                                                        .catch(console.error)
                                                }}
                                            >Administrer Betaling</LoadingButton>
                                        ) : (
                                            <Button
                                                fullWidth
                                                size="small"
                                                color="secondary"
                                                variant="contained"
                                                component={RouterLink}
                                                to={Paths.ORG_USERS + '/' + subscription.metadata.orgUserId}
                                            >
                                                Kontakt Behandler
                                            </Button>
                                        )}
                                    </ListItem>
                                </>
                            )
                        })}
                        {
                            appUser && orgUser && org && (
                                <>
                                    {products?.map(product => {
                                        if (subscriptions?.find(sub => sub.product.id === product.id)) {
                                            return null
                                        }
                                        return (
                                            <React.Fragment key={product.id}>
                                                <ListSubheader key={product.id + '-subheader'} style={{ lineHeight: '10px', marginTop: '20px' }}>{product.name}</ListSubheader>
                                                {
                                                    product.prices.map((price) => {
                                                        return (
                                                            <ListItem key={price.id}>
                                                                <LoadingButton
                                                                    size="small"
                                                                    ariaLabel={"subscribe-" + product.name + '-' + price.id}
                                                                    color="primary"
                                                                    onClick={() => {
                                                                        return subscribe(appUser, orgUser, org, price.id, enqueueSnackbar)
                                                                            .then(setCustomerPortalLink)
                                                                            .catch(console.error)
                                                                    }}
                                                                >Abonner {price.interval === 'month' ? `${formatMoney(price.unit_amount / 100)}/${price.interval === 'month' ? 'mnd' : ''}/bruker` : 'Annet'}</LoadingButton>
                                                            </ListItem>
                                                        );
                                                    })
                                                }
                                            </React.Fragment>
                                        )
                                    })}
                                </>
                            )}
                    </List>
                </CardContent>
            </Card>
        </ListItem >
    )
}
